import { useFetcher, useLocation } from "@remix-run/react";
import { useEffect } from "react";
import isNil from "~/kernel/validation/isNil";
import type { ActiveSystemMessageLoaderData } from "~/routes/system-messages.active.json/ActiveSystemMessageLoaderData";
import { Banner } from "~/ui/Banner";
import type { SystemMessage } from "../models";
import ROUTES from "~/kernel/routing/ROUTES";
import useLocalStorageItem from "~/kernel/localStorage/useLocalStorageItem";
import toBoolean from "~/kernel/form/toBoolean";
import toBooleanString from "~/kernel/form/toBooleanString";

function useIsDismissed(id: string) {
  const [value, setValue] = useLocalStorageItem(`dismissedSystemMessage.${id}`);
  return [
    toBoolean(value),
    (newValue: boolean) => setValue(toBooleanString(newValue)),
  ] as const;
}

export function SystemMessage() {
  const fetcher = useFetcher<ActiveSystemMessageLoaderData>();

  useEffect(() => {
    if (isNil(fetcher.data) && fetcher.state === "idle") {
      fetcher.load(ROUTES.activeSystemMessage.path());
    }
  }, [fetcher]);

  const message = fetcher.data?.message;

  if (isNil(message)) {
    return null;
  }

  return (
    <Content
      message={{
        ...message,
        pathPattern: message.pathPattern,
        activateAt: new Date(message.activateAt),
        disableAt: new Date(message.disableAt),
      }}
    />
  );
}

function Content({ message }: { message: SystemMessage }) {
  const loc = useLocation();
  const pattern = new RegExp(message.pathPattern);
  const [isDismissed, setIsDismissed] = useIsDismissed(message.id);

  if (isDismissed) {
    return null;
  }

  if (!loc.pathname.match(pattern)) {
    return null;
  }

  return (
    <Banner onDismiss={() => setIsDismissed(true)} variant={message.variant}>
      {message.content}
    </Banner>
  );
}
